import { useState, useEffect } from 'react';
import Navbar from './components/Navbar';

interface Project {
  id: string;
  title: string;
  category: string;
  description: string;
  imageUrl: string;
  completedDate: string;
}

export default function Admin() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    category: 'new-home',
    description: '',
    imageUrl: '',
    completedDate: ''
  });

  useEffect(() => {
    const savedProjects = localStorage.getItem('lineflow-projects');
    if (savedProjects) {
      setProjects(JSON.parse(savedProjects));
    }
  }, []);

  const saveProjects = (updatedProjects: Project[]) => {
    setProjects(updatedProjects);
    localStorage.setItem('lineflow-projects', JSON.stringify(updatedProjects));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isEditing && editingId) {
      const updatedProjects = projects.map(p =>
        p.id === editingId ? { ...formData, id: editingId } : p
      );
      saveProjects(updatedProjects);
      setIsEditing(false);
      setEditingId(null);
    } else {
      const newProject: Project = {
        ...formData,
        id: Date.now().toString()
      };
      saveProjects([...projects, newProject]);
    }

    setFormData({
      title: '',
      category: 'new-home',
      description: '',
      imageUrl: '',
      completedDate: ''
    });
  };

  const handleEdit = (project: Project) => {
    setFormData({
      title: project.title,
      category: project.category,
      description: project.description,
      imageUrl: project.imageUrl,
      completedDate: project.completedDate
    });
    setIsEditing(true);
    setEditingId(project.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      saveProjects(projects.filter(p => p.id !== id));
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditingId(null);
    setFormData({
      title: '',
      category: 'new-home',
      description: '',
      imageUrl: '',
      completedDate: ''
    });
  };

  const categories = [
    { id: 'new-home', label: 'New Home Plumbing' },
    { id: 'underground', label: 'Underground Piping' },
    { id: 'civil', label: 'Civil Engineering' },
    { id: 'commercial', label: 'Commercial Projects' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navbar />

      <div className="pt-24 pb-16">
        <div className="max-w-[1400px] mx-auto px-6">
          {/* Header */}
          <div className="mb-12">
            <h1 className="text-4xl font-black text-[#0F4C81] mb-4">Project Management</h1>
            <p className="text-lg text-gray-600">Add, edit, and manage your project portfolio</p>
          </div>

          {/* Form Section */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-12 border-2 border-[#FF6B35]/20">
            <h2 className="text-2xl font-bold text-[#0F4C81] mb-6">
              {isEditing ? 'Edit Project' : 'Add New Project'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="title" className="block text-sm font-semibold text-gray-700 mb-2">
                    Project Title *
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#FF6B35] focus:outline-none transition-colors"
                    placeholder="Enter project title"
                  />
                </div>

                <div>
                  <label htmlFor="category" className="block text-sm font-semibold text-gray-700 mb-2">
                    Category *
                  </label>
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#FF6B35] focus:outline-none transition-colors cursor-pointer"
                  >
                    <option value="">Select category</option>
                    <option value="New Home Plumbing">New Home Plumbing</option>
                    <option value="Underground Piping">Underground Piping</option>
                    <option value="Civil Engineering">Civil Engineering</option>
                    <option value="Commercial Projects">Commercial Projects</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-semibold text-gray-700 mb-2">
                  Description *
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows={4}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#FF6B35] focus:outline-none transition-colors resize-none"
                  placeholder="Describe the project..."
                ></textarea>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="imageUrl" className="block text-sm font-semibold text-gray-700 mb-2">
                    Image URL *
                  </label>
                  <input
                    type="url"
                    id="imageUrl"
                    name="imageUrl"
                    value={formData.imageUrl}
                    onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#FF6B35] focus:outline-none transition-colors"
                    placeholder="https://example.com/image.jpg"
                  />
                </div>

                <div>
                  <label htmlFor="completedDate" className="block text-sm font-semibold text-gray-700 mb-2">
                    Completion Date *
                  </label>
                  <input
                    type="date"
                    id="completedDate"
                    name="completedDate"
                    value={formData.completedDate}
                    onChange={(e) => setFormData({ ...formData, completedDate: e.target.value })}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#FF6B35] focus:outline-none transition-colors cursor-pointer"
                  />
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  type="submit"
                  className="px-8 py-3 bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] text-white font-bold rounded-xl hover:shadow-lg hover:scale-105 transition-all whitespace-nowrap cursor-pointer"
                >
                  {isEditing ? 'Update Project' : 'Add Project'}
                </button>
                {isEditing && (
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="px-8 py-3 bg-gray-200 text-gray-700 font-bold rounded-xl hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* Projects List */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <h2 className="text-2xl font-bold text-[#0F4C81] mb-6">Current Projects</h2>

            {projects.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-4">
                  <i className="ri-folder-open-line text-4xl text-gray-400"></i>
                </div>
                <p className="text-gray-500">No projects yet. Add your first project above.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {projects.map(project => (
                  <div
                    key={project.id}
                    className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow border-2 border-transparent hover:border-[#FF6B35]/30"
                  >
                    <div className="flex justify-between items-start mb-4">
                      <span className="px-3 py-1 bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] text-white text-xs font-bold rounded-full">
                        {project.category}
                      </span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(project)}
                          className="w-8 h-8 flex items-center justify-center bg-[#0F4C81] text-white rounded-lg hover:bg-[#1E88E5] transition-colors cursor-pointer"
                        >
                          <i className="ri-edit-line"></i>
                        </button>
                        <button
                          onClick={() => handleDelete(project.id)}
                          className="w-8 h-8 flex items-center justify-center bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors cursor-pointer"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </div>
                    </div>
                    <h3 className="text-lg font-bold text-[#0F4C81] mb-2">{project.title}</h3>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{project.description}</p>
                    <div className="text-xs text-gray-500">
                      <i className="ri-calendar-line text-[#FF6B35]"></i> {new Date(project.completedDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
